import AddIcon from "@mui/icons-material/Add";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { AccountPlan, Organization, Paginated, Project } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useOrganization } from "../lib/useOrganization";

const STATUSY: Record<string, { label: string; color: "default" | "success" | "warning" }> = {
  draft: { label: "Przygotowanie", color: "default" },
  active: { label: "W realizacji", color: "success" },
  closed: { label: "Zamknięty", color: "default" },
  cancelled: { label: "Anulowany", color: "warning" },
};

export function ProjectsPage() {
  const { can } = useAuth();
  const { organization } = useOrganization();
  const [otwarty, setOtwarty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["projects", organization],
    queryFn: () =>
      api.get<Paginated<Project>>(
        `/projects${organization ? `?organization=${encodeURIComponent(organization)}` : ""}`,
      ),
  });

  const projekty = data?.results ?? [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center">
        <Typography variant="h6">Projekty</Typography>
        <Box sx={{ flex: 1 }} />
        {can("project.create") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOtwarty(true)}>
            Nowy projekt
          </Button>
        )}
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kod</TableCell>
              <TableCell>Nazwa</TableCell>
              <TableCell>Podmiot</TableCell>
              <TableCell>Klient</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Termin</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projekty.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>
                  <Link to={`/projekty/${p.code}`}>{p.code}</Link>
                </TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.organization_name}</TableCell>
                <TableCell>{p.client_name || "—"}</TableCell>
                <TableCell>
                  <Chip size="small" label={STATUSY[p.status]?.label ?? p.status}
                        color={STATUSY[p.status]?.color ?? "default"} />
                </TableCell>
                <TableCell>{p.starts_on ?? "—"}</TableCell>
              </TableRow>
            ))}
            {!isLoading && projekty.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Brak projektów w zasięgu Twoich uprawnień.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <NowyProjekt open={otwarty} onClose={() => setOtwarty(false)} />
    </Stack>
  );
}

function NowyProjekt({ open, onClose }: { open: boolean; onClose: () => void }) {
  const kolejka = useQueryClient();
  const pusty = { code: "", name: "", client_name: "", organization: "", account_plan: "" };
  const [form, setForm] = useState(pusty);
  const [blad, setBlad] = useState<ApiError | null>(null);

  const { data: podmioty } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api.get<Paginated<Organization>>("/organizations"),
    enabled: open,
  });
  const { data: plany } = useQuery({
    queryKey: ["account-plans"],
    queryFn: () => api.get<Paginated<AccountPlan>>("/catalog/account-plans"),
    enabled: open,
  });
  const planDomyslny = plany?.results.find((p) => p.is_default)?.code ?? "";

  const zapisz = useMutation({
    mutationFn: () =>
      api.post<Project>("/projects", {
        ...form,
        account_plan: form.account_plan || planDomyslny,
        slug: form.code.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      }),
    onSuccess: () => {
      kolejka.invalidateQueries({ queryKey: ["projects"] });
      setForm(pusty);
      setBlad(null);
      onClose();
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nowy projekt</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {blad && !Object.keys(blad.body.fields).length && (
            <Alert severity="error">{blad.message}</Alert>
          )}
          <TextField
            label="Kod" value={form.code} error={Boolean(blad?.field("code"))}
            helperText={blad?.field("code")}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
          />
          <TextField
            label="Nazwa" value={form.name} error={Boolean(blad?.field("name"))}
            helperText={blad?.field("name")}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Klient" value={form.client_name}
            onChange={(e) => setForm({ ...form, client_name: e.target.value })}
          />
          <TextField
            select label="Podmiot" value={form.organization}
            error={Boolean(blad?.field("organization"))}
            helperText={blad?.field("organization") ?? "Spółka, która poniesie koszty."}
            onChange={(e) => setForm({ ...form, organization: e.target.value })}
          >
            {(podmioty?.results ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select label="Plan kont" value={form.account_plan || planDomyslny}
            error={Boolean(blad?.field("account_plan"))}
            helperText={blad?.field("account_plan")
              ?? "Słownik pozycji kosztorysu. Po założeniu projektu nie zmienia się."}
            onChange={(e) => setForm({ ...form, account_plan: e.target.value })}
          >
            {(plany?.results ?? []).map((p) => (
              <MenuItem key={p.id} value={p.code}>{p.name}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={() => zapisz.mutate()} disabled={zapisz.isPending}>
          Utwórz
        </Button>
      </DialogActions>
    </Dialog>
  );
}
